import { lazy, Suspense, useCallback } from 'react';

import { useComposeSnapshotIndexHydration } from '../../hooks/useComposeSnapshotIndexHydration';
import { useComposeSnapshotSync } from '../../hooks/useComposeSnapshotSync';
import { useMissingSnapshotNotice } from '../../hooks/useMissingSnapshotNotice';
import { useOrphanSnapshotSweep } from '../../hooks/useOrphanSnapshotSweep';
import { useUnsentComposeRecovery } from '../../hooks/useUnsentComposeRecovery';
import { useMessageDialogStore } from '../../store';

// Lazy-loaded so the TipTap editor stack (~114 KB) only downloads when the
// compose/reply/forward dialog is actually opened — not on every page load.
const MessageDialog = lazy(() =>
  import('../../pages/private/Inbox/components/MessageDialog').then(
    (module) => ({ default: module.MessageDialog }),
  ),
);

export function GlobalMessageDialog() {
  const isOpen = useMessageDialogStore((state) => state.isOpen);
  const closeIfCurrent = useMessageDialogStore((state) => state.closeIfCurrent);
  const dialogKey = useMessageDialogStore((state) => state.dialogKey);
  const closeThisDialog = useCallback(
    () => closeIfCurrent(dialogKey),
    [closeIfCurrent, dialogKey],
  );
  const messageId = useMessageDialogStore((state) => state.messageId);
  const draftId = useMessageDialogStore((state) => state.draftId);
  const selectedHandlerButton = useMessageDialogStore(
    (state) => state.selectedHandlerButton,
  );
  const inboxEntryId = useMessageDialogStore((state) => state.inboxEntryId);
  const scheduledEmail = useMessageDialogStore((state) => state.scheduledEmail);
  const composeToRecipients = useMessageDialogStore(
    (state) => state.composeToRecipients,
  );
  const aiAssisted = useMessageDialogStore((state) => state.aiAssisted);
  const prefill = useMessageDialogStore((state) => state.prefill);
  const onSent = useMessageDialogStore((state) => state.onSent);
  const restoreSnapshotSessionId = useMessageDialogStore(
    (state) => state.restoreSnapshotSessionId,
  );
  useUnsentComposeRecovery();
  useComposeSnapshotIndexHydration();
  useComposeSnapshotSync();
  useOrphanSnapshotSweep();
  useMissingSnapshotNotice();

  if (!isOpen) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <MessageDialog
        key={dialogKey}
        dialogId={String(dialogKey)}
        onClose={closeThisDialog}
        messageId={messageId}
        draftId={draftId}
        selectedHandlerButton={selectedHandlerButton}
        onResetHandler={closeThisDialog}
        inboxEntryId={inboxEntryId}
        scheduledEmail={scheduledEmail}
        composeToRecipients={composeToRecipients}
        aiAssisted={aiAssisted}
        prefill={prefill}
        onSent={onSent}
        restoreSnapshotSessionId={restoreSnapshotSessionId}
      />
    </Suspense>
  );
}
